import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, XAxis, YAxis, Bar, CartesianGrid } from 'recharts';
import { ChartPie, FilePdf, Buildings, Clock, CurrencyEur } from '@phosphor-icons/react';
import ProjectDistributionCharts from '../components/dashboard/ProjectDistributionCharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PALETTE = ['#002FA7', '#2E7D32', '#EF6C00', '#9C27B0', '#D32F2F', '#1565C0', '#E65100', '#6A1B9A', '#00838F', '#F57F17', '#5D4037', '#546E7A'];

const StatsBlock = ({ title, total, unit, data, color }) => (
  <div style={{ flex: 1, minWidth: '320px', background: 'white', border: '1px solid #E2E2E6', borderRadius: '10px', padding: '18px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <ChartPie size={16} color={color} weight="fill" />
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700' }}>{title}</h4>
    </div>
    <div style={{ fontSize: '1.35rem', fontWeight: '800', color, marginBottom: '6px' }}>
      {unit === 'EUR' ? `${(total || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR` : `${(total || 0).toFixed(1)} h`}
    </div>
    {data.length === 0 ? (
      <div style={{ padding: '30px', textAlign: 'center', color: '#9A9AA0', fontSize: '0.85rem' }}>Aucune donnée</div>
    ) : (
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}
               label={(e) => `${e.name} (${((e.value / total) * 100).toFixed(0)}%)`}>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => unit === 'EUR' ? `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR` : `${Number(v).toFixed(1)} h`} />
          <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: '0.72rem' }} />
        </PieChart>
      </ResponsiveContainer>
    )}
  </div>
);

const MonthlyChart = ({ expensesByMonth, hoursByMonth }) => {
  const months = Array.from(new Set([...expensesByMonth.map(m => m.name), ...hoursByMonth.map(m => m.name)])).sort();
  const expMap = Object.fromEntries(expensesByMonth.map(m => [m.name, m.value]));
  const hrsMap = Object.fromEntries(hoursByMonth.map(m => [m.name, m.value]));
  const data = months.map(m => ({ mois: m, Depenses: expMap[m] || 0, Heures: hrsMap[m] || 0 }));
  if (!data.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '10px', padding: '18px', marginTop: '16px' }}>
      <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', fontWeight: '700' }}>Évolution mensuelle</h4>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="mois" style={{ fontSize: '0.72rem' }} />
          <YAxis yAxisId="left" orientation="left" stroke="#002FA7" style={{ fontSize: '0.72rem' }} />
          <YAxis yAxisId="right" orientation="right" stroke="#EF6C00" style={{ fontSize: '0.72rem' }} />
          <Tooltip formatter={(v, n) => n === 'Depenses' ? `${Number(v).toFixed(2)} EUR` : `${Number(v).toFixed(1)} h`} />
          <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
          <Bar yAxisId="left" dataKey="Depenses" fill="#002FA7" radius={[4, 4, 0, 0]} />
          <Bar yAxisId="right" dataKey="Heures" fill="#EF6C00" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const StatsPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [finance, setFinance] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      .then(r => setProjects(r.data || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/dashboard/finance`, { withCredentials: true })
      .then(r => setFinance(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) { setData(null); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/projects/stats/project/${selectedId}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => toast.error('Erreur chargement statistiques'))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const handleDownload = async () => {
    if (!selectedId) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/projects/stats/project/${selectedId}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `stats_${data?.project_name || 'chantier'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error('Erreur telechargement'); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="stats-page">
      {finance && (
        <div data-testid="stats-ca-banner" style={{
          padding: '18px 24px',
          background: 'linear-gradient(135deg, #002FA7 0%, #3D5AFE 55%, #5C7EFF 100%)',
          color: 'white',
          borderRadius: '10px',
          marginBottom: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CurrencyEur size={22} weight="bold" />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1.2px', opacity: 0.85, fontWeight: '700' }}>
                Chiffre d'affaires total {finance.annee_courante || new Date().getFullYear()}
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: '800', marginTop: '2px', lineHeight: 1 }} data-testid="stats-ca-amount">
                {(finance.ca_annee_courante_ht || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} &euro;
                <span style={{ fontSize: '0.8rem', fontWeight: '500', opacity: 0.9, marginLeft: '8px' }}>HT</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.15)', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.68rem', opacity: 0.85, textTransform: 'uppercase', fontWeight: '700' }}>Dépenses HT</div>
              <div style={{ fontSize: '1rem', fontWeight: '700' }}>{(finance.depenses_globales_ht || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} &euro;</div>
            </div>
            <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.15)', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.68rem', opacity: 0.85, textTransform: 'uppercase', fontWeight: '700' }}>Reste à facturer</div>
              <div style={{ fontSize: '1rem', fontWeight: '700' }}>{(finance.reste_a_facturer_ht || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} &euro;</div>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <h2 style={{ margin: 0, fontSize: '1.2rem', flex: '0 0 auto' }}>Statistiques</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.85rem', color: '#5E5F66', fontWeight: '600' }}>Chantier :</label>
          <select className="form-input" style={{ minWidth: '240px', maxWidth: '360px' }}
                  value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="stats-project-select">
            <option value="">Tous les chantiers (vue globale)</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name} {p.archived ? '(archivé)' : ''}</option>)}
          </select>
          {selectedId && (
            <button className="btn btn-primary btn-sm" onClick={handleDownload} disabled={downloading} data-testid="download-stats-pdf-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <FilePdf size={16} /> {downloading ? 'Téléchargement...' : 'Télécharger le rapport PDF'}
            </button>
          )}
        </div>
      </div>

      {!selectedId ? (
        <>
          <p style={{ marginTop: 0, fontSize: '0.85rem', color: '#5E5F66' }}>Vue globale : dépenses et heures consommées sur tous les chantiers en cours.</p>
          <ProjectDistributionCharts />
        </>
      ) : loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : data ? (
        <div data-testid="stats-project-detail">
          {/* KPIs */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ flex: 1, minWidth: '200px', padding: '16px 18px', background: 'linear-gradient(135deg, #002FA7, #3D5AFE)', color: 'white', borderRadius: '10px' }}>
              <div style={{ fontSize: '0.72rem', opacity: 0.85, textTransform: 'uppercase', fontWeight: '700' }}><Buildings size={12} weight="fill" /> Chantier</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', marginTop: '4px' }}>{data.project_name}</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>{data.client_name || '-'}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '6px' }}>Vente : {data.sale_price_ht.toLocaleString('fr-FR')} EUR HT</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px', padding: '16px 18px', background: 'white', border: `2px solid ${data.pct_expenses >= 95 ? '#C62828' : '#E2E2E6'}`, borderRadius: '10px' }}>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase', fontWeight: '700' }}><CurrencyEur size={12} /> Dépenses vs Budget</div>
              <div style={{ fontSize: '1.35rem', fontWeight: '800', color: data.pct_expenses >= 95 ? '#C62828' : '#002FA7' }}>{data.total_expenses_ht.toLocaleString('fr-FR')} EUR</div>
              {data.total_budget_ht > 0
                ? <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>Budget : {data.total_budget_ht.toLocaleString('fr-FR')} EUR ({data.pct_expenses}%)</div>
                : <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>Aucun budget défini</div>}
            </div>
            <div style={{ flex: 1, minWidth: '200px', padding: '16px 18px', background: 'white', border: `2px solid ${data.pct_invoiced >= 100 ? '#2E7D32' : '#E2E2E6'}`, borderRadius: '10px' }}>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase', fontWeight: '700' }}>💳 Facturation</div>
              <div style={{ fontSize: '1.35rem', fontWeight: '800', color: data.pct_invoiced >= 100 ? '#2E7D32' : '#002FA7' }}>{data.total_invoiced_ht.toLocaleString('fr-FR')} EUR</div>
              <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>
                {data.invoices_count} situation(s) · {data.pct_invoiced}% facturé
              </div>
              <div style={{ fontSize: '0.75rem', color: data.remaining_ht >= 0 ? '#2E7D32' : '#C62828', fontWeight: '600' }}>Reste : {data.remaining_ht.toLocaleString('fr-FR')} EUR</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px', padding: '16px 18px', background: 'white', border: `2px solid ${data.pct_hours >= 95 ? '#C62828' : '#E2E2E6'}`, borderRadius: '10px' }}>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase', fontWeight: '700' }}><Clock size={12} /> Heures</div>
              <div style={{ fontSize: '1.35rem', fontWeight: '800', color: data.pct_hours >= 95 ? '#C62828' : '#002FA7' }}>{data.total_hours.toFixed(1)} h</div>
              {data.labor_hours_quota > 0 && <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>/ {data.labor_hours_quota.toFixed(1)} h ({data.pct_hours}%)</div>}
              <div style={{ fontSize: '0.75rem', color: data.margin_estimated_ht >= 0 ? '#2E7D32' : '#C62828', fontWeight: '600', marginTop: '6px' }}>Marge estimée : {data.margin_estimated_ht.toLocaleString('fr-FR')} EUR</div>
            </div>
          </div>

          {/* Pies */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <StatsBlock title="Dépenses par catégorie" total={data.total_expenses_ht} unit="EUR" data={data.expenses_by_category} color="#002FA7" />
            <StatsBlock title="Heures par salarié" total={data.total_hours} unit="h" data={data.hours_by_user} color="#EF6C00" />
          </div>

          <MonthlyChart expensesByMonth={data.expenses_by_month} hoursByMonth={data.hours_by_month} />
        </div>
      ) : null}
    </div>
  );
};

export default StatsPage;
